import * as _ from 'lodash';
import * as moment from 'moment';
import * as qs from 'query-string';
import { Component } from 'react';
import { Dispatch, connect } from 'react-redux';
import { AppActionCreators, IApplicationState, IMissedMatchOffersResponse, ReportActionCreators } from '@nkr/store/index';
import { IMissedMatchOffer } from '@nkr/models/index';
import { CsvExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { Button, FormControl, Well } from 'react-bootstrap';
import Grid from './grid';
import AuditReportScoreTable from '../audit-report-score-table';
import { MissedMatchOfferDeadlineOverlay } from '@nkr/views/center-admin/action-center/center-performance/relative-performance-overlays';
import { IRpMissedMatchOfferDeadline } from '@nkr/models/center-performance/relative-performance';

interface IOwnProps {
    centerId: number,
    isCenterLogin: boolean
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getMissedMatchOffers: (startDate: string, endDate: string) => Promise<IMissedMatchOffersResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: IMissedMatchOffer[],
    startDate: string,
    endDate: string,
    recipientCenterId: number,
    gridApi: GridApi,
}

class MissedMatchOffers extends Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        startDate: '',
        endDate: '',
        recipientCenterId: 0,
        gridApi: null
    }

    componentDidMount = () => {
        let parsed = qs.parse(location.search);
        let startDate = moment().add(-90, 'days').format('YYYY-MM-DD');
        let endDate = moment().format('YYYY-MM-DD');

        if (parsed.sd && typeof parsed.sd == 'string') {
            startDate = parsed.sd;
        }

        if (parsed.ed && typeof parsed.ed == 'string') {
            endDate = parsed.ed;
        }

        this.getData(startDate, endDate);
    }

    getData = (startDate: string, endDate: string) => {
        let recipientCenterId = this.props.isCenterLogin ? this.props.centerId : 0;

        this.props.showLoading('Getting missed match offers, one moment please...').then(() => {
            this.props.getMissedMatchOffers(startDate, endDate).then(response => {
                this.setState({
                    data: response.data.results,
                    startDate,
                    endDate,
                    recipientCenterId
                }, () => {
                    this.setUrlParameters();
                    this.props.hideLoading();
                });
            });
        });
    }

    setUrlParameters = () => {
        let paramList = [];

        if (this.state.startDate != '') {
            paramList.push('sd=' + this.state.startDate);
        }

        if (this.state.endDate != '') {
            paramList.push('ed=' + this.state.endDate);
        }

        let params = paramList.join('&');
        let newUrl = `/center/${this.props.centerId}/reports/my-center/missed-match-offers?${params}`;

        if (this.props.centerId <= 0) {
            newUrl = `/admin/reports/all-centers/missed-match-offers?${params}`;
        }

        window.history.pushState({}, '', newUrl);
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'startDate') {
            this.setState({
                startDate: value
            });
        }

        if (name == 'endDate') {
            this.setState({
                endDate: value
            });
        }
    }

    handleDateRangeSearch = () => {
        this.getData(this.state.startDate, this.state.endDate);
    }

    handleRecipientCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            recipientCenterId: parseInt(e.currentTarget.value)
        });
    }

    onGridReady = (event: GridReadyEvent) => {
        this.setState({
            gridApi: event.api
        })
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'missed-match-offer.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();
                if (colDef.colId == "decision") {
                    let data = params.node.data as IMissedMatchOffer;

                    if (data.wasAcceptedNoIssues) {
                        return 'Accepted - No Issues';
                    }

                    if (data.wasDeclinedUnacceptable) {
                        return 'Declined';
                    }

                    if (data.wasAcceptedNotConfident) {
                        return 'Accepted - Not Confident';
                    }

                    if (data.wasDeclinedUnacceptableAbove99Cpra) {
                        return 'Declined - Above 99 cPRA';
                    }

                    if (data.wasAcceptedNotConfidentAbove99Cpra) {
                        return 'Accepted - Not Confident - Above 99 cPRA';
                    }

                    if (data.wasDeclinedSurgeonUnavailable) {
                        return 'Declined - Surgeon Unavailable';
                    }

                    return '';
                }

                return params.value;

            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const filteredData = this.state.data.filter(x => {
            let recipientCenterFilter = this.state.recipientCenterId > 0 ? x.recipientCenterId == this.state.recipientCenterId : true;
            return recipientCenterFilter;
        });

        const sortedData = _.orderBy(filteredData, [
            item => item.recipientAlias
        ], ['asc']);

        const recipientCenterList = _.sortBy(_.uniqBy(this.state.data.map(item => {
            return { id: item.recipientCenterId, alias: item.recipientCenter };
        }), item => item.id), item => item.alias);

        const missedOfferDeadline: IRpMissedMatchOfferDeadline = {
            matchOfferCount: filteredData.length,
            missedDeadlineCount: filteredData.filter(x => x.deadlineMissed).length,
            rank: 0,
            ratio: 0,
            category: ''
        };

        return (
            <>
                <Well bsSize='sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}>Start Date:</div>
                            <div>
                                <FormControl
                                    type='date'
                                    name='startDate'
                                    value={this.state.startDate}
                                    onChange={this.handleDateChange}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}>End Date:</div>
                            <div>
                                <FormControl
                                    type='date'
                                    name='endDate'
                                    value={this.state.endDate}
                                    onChange={this.handleDateChange}
                                />
                            </div>
                        </div>

                        <div style={{ marginRight: 20 }}>
                            <Button bsStyle='primary' onClick={this.handleDateRangeSearch}>Search</Button>
                        </div>

                        {this.props.isCenterLogin == false && (
                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                <div style={{ marginRight: 5 }}>Recipient Center:</div>
                                <div style={{ marginRight: 20 }}>
                                    <FormControl
                                        componentClass='select'
                                        value={this.state.recipientCenterId}
                                        onChange={this.handleRecipientCenterChange}>
                                        <option value={0}>- All Centers -</option>
                                        {recipientCenterList.map(center =>
                                            <option key={`ctr-${center.id}`} value={center.id}>{center.alias}</option>
                                        )}
                                    </FormControl>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <AuditReportScoreTable
                            reportName='Missed Match Offer Deadline - (Last 90 Days)'
                            scoreValue={`${(missedOfferDeadline.missedDeadlineCount / missedOfferDeadline.matchOfferCount).toFixed(2)}%`}
                            overlay={MissedMatchOfferDeadlineOverlay({ missedMatchOfferDeadline: missedOfferDeadline })}
                        />
                    </div>

                    <div style={{ marginRight: 5, float: 'right', display: 'flex' }}>
                        <Button onClick={this.handleExport}>Export CSV</Button>
                    </div>
                </Well>

                <Grid
                    data={sortedData}
                    isCenterLogin={this.props.isCenterLogin}
                    onGridReady={this.onGridReady}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getMissedMatchOffers: (startDate, endDate) => {
            return dispatch(ReportActionCreators.getMissedMatchOffers(startDate, endDate));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(MissedMatchOffers);
